import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  deleteLuyouWorkbookTemplate,
  getLuyouWorkbookTemplate,
  uploadLuyouWorkbookTemplate,
  validateLuyouWorkbookTemplateFile,
  type LuyouWorkbookTemplate,
} from '../lib/admin-luyou-workbook-template-api';
import { useAdminAuthStore } from '../stores/useAdminAuthStore';
import { YOUTU_BRAND_NAME } from '../lib/youtu-brand';

function formatBytes(value: number | null): string {
  if (value == null) {
    return '—';
  }
  if (value < 1024) {
    return `${value} B`;
  }
  return `${(value / 1024 / 1024).toFixed(2)} MiB`;
}

function formatDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('zh-CN', { hour12: false });
}

export default function LuyouWorkbookTemplatePage() {
  const accessToken = useAdminAuthStore((state) => state.accessToken);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [template, setTemplate] = useState<LuyouWorkbookTemplate | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadTemplate = useCallback(async () => {
    if (!accessToken) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    try {
      setTemplate(await getLuyouWorkbookTemplate(accessToken));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '模板信息加载失败。');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void loadTemplate();
  }, [loadTemplate]);

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    setSuccessMessage(null);
    if (!file) {
      setSelectedFile(null);
      return;
    }

    const validationMessage = validateLuyouWorkbookTemplateFile(file);
    if (validationMessage) {
      setSelectedFile(null);
      setErrorMessage(validationMessage);
      event.target.value = '';
      return;
    }

    setSelectedFile(file);
    setErrorMessage(null);
  }

  async function handleUpload() {
    if (!accessToken || !selectedFile) {
      return;
    }

    setIsUploading(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      const nextTemplate = await uploadLuyouWorkbookTemplate(accessToken, selectedFile);
      setTemplate(nextTemplate);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSuccessMessage('模板已更新。新模板只会用于今后首次创建的项目表格。');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '模板上传失败。');
    } finally {
      setIsUploading(false);
    }
  }

  async function handleDelete() {
    if (!accessToken || !template?.configured) {
      return;
    }
    if (!window.confirm('确认删除当前模板？已经创建的项目表格不会受到影响。')) {
      return;
    }

    setIsDeleting(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      await deleteLuyouWorkbookTemplate(accessToken);
      setTemplate({
        configured: false,
        profileId: 'luyou_expert',
        revision: null,
        filename: null,
        bytesSize: null,
        updatedAt: null,
      });
      setSuccessMessage('模板已删除。今后新建的项目表格将从空白表格开始。');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '模板删除失败。');
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <main className="min-h-screen bg-stone-100 text-stone-950">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
        <header className="flex flex-col gap-4 border-b border-stone-300 pb-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-medium text-emerald-700">{YOUTU_BRAND_NAME} · 管理后台</p>
            <h1 className="mt-2 text-3xl font-semibold">Excel 表格模板</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-600">
              为 <code className="rounded bg-stone-200 px-1 py-0.5">luyou_expert</code>{' '}
              配置公开模板。用户第一次打开某个项目的表格时会获得一份独立副本。
            </p>
          </div>
          <Link
            className="w-fit rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-medium text-stone-700 shadow-sm transition hover:border-emerald-400 hover:text-emerald-800"
            to="/admin"
          >
            返回后台
          </Link>
        </header>

        {errorMessage ? (
          <div
            className="flex flex-col gap-3 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800 sm:flex-row sm:items-center sm:justify-between"
            role="alert"
          >
            <span>{errorMessage}</span>
            {!template && !isLoading ? (
              <button
                className="w-fit font-semibold underline underline-offset-4"
                onClick={() => void loadTemplate()}
                type="button"
              >
                重试
              </button>
            ) : null}
          </div>
        ) : null}

        {successMessage ? (
          <div
            className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
            role="status"
          >
            {successMessage}
          </div>
        ) : null}

        <section className="rounded-2xl border border-stone-300 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">当前模板</h2>
              <p className="mt-1 text-sm text-stone-600">
                {isLoading
                  ? '正在加载模板信息…'
                  : template?.configured
                    ? '已配置，后续新项目将以此为初始内容。'
                    : '未配置，后续新项目将从空白表格开始。'}
              </p>
            </div>
            {!isLoading ? (
              <span
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                  template?.configured
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                    : 'border-stone-300 bg-stone-100 text-stone-600'
                }`}
              >
                {template?.configured ? '已配置' : '未配置'}
              </span>
            ) : null}
          </div>

          {template?.configured ? (
            <dl className="mt-5 grid gap-4 rounded-xl bg-stone-50 p-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium text-stone-500">文件名</dt>
                <dd className="mt-1 break-all text-sm font-medium">{template.filename ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-stone-500">文件大小</dt>
                <dd className="mt-1 text-sm font-medium">{formatBytes(template.bytesSize)}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-stone-500">模板版本</dt>
                <dd className="mt-1 text-sm font-medium">
                  {template.revision == null ? '—' : `v${template.revision}`}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-stone-500">更新时间</dt>
                <dd className="mt-1 text-sm font-medium">{formatDateTime(template.updatedAt)}</dd>
              </div>
            </dl>
          ) : null}
        </section>

        <section className="rounded-2xl border border-stone-300 bg-white p-5 shadow-sm sm:p-6">
          <h2 className="text-lg font-semibold">
            {template?.configured ? '替换模板' : '上传模板'}
          </h2>
          <p className="mt-2 text-sm leading-6 text-stone-600">
            仅支持不超过 5 MiB 的 .xlsx 文件。宏、外部连接和无效文件会由服务器拒绝。
          </p>

          <div className="mt-5 rounded-xl border border-dashed border-stone-300 bg-stone-50 p-4">
            <label className="block text-sm font-medium" htmlFor="luyou-workbook-template-file">
              选择 Excel 文件
            </label>
            <input
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="mt-3 block w-full text-sm text-stone-700 file:mr-4 file:rounded-lg file:border-0 file:bg-stone-900 file:px-4 file:py-2 file:font-semibold file:text-white hover:file:bg-stone-700"
              disabled={isUploading || isDeleting}
              id="luyou-workbook-template-file"
              onChange={handleFileChange}
              ref={fileInputRef}
              type="file"
            />
            {selectedFile ? (
              <p className="mt-3 text-sm text-stone-700">
                已选择：<span className="font-medium">{selectedFile.name}</span>（{formatBytes(selectedFile.size)}）
              </p>
            ) : null}
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!accessToken || !selectedFile || isUploading || isDeleting}
              onClick={() => void handleUpload()}
              type="button"
            >
              {isUploading ? '上传中…' : template?.configured ? '上传并替换' : '上传模板'}
            </button>
            {template?.configured ? (
              <button
                className="rounded-lg border border-rose-300 bg-white px-4 py-2 text-sm font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!accessToken || isUploading || isDeleting}
                onClick={() => void handleDelete()}
                type="button"
              >
                {isDeleting ? '删除中…' : '删除模板'}
              </button>
            ) : null}
          </div>
        </section>

        <aside className="rounded-2xl border border-amber-300 bg-amber-50 p-5 text-sm leading-6 text-amber-950">
          <h2 className="font-semibold">生效范围与兼容性</h2>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>替换或删除模板只影响之后首次创建的表格，不会覆盖任何已有项目表格。</li>
            <li>在线编辑器不会完整保留 Excel 的图表、图片、批注、宏和其他高级功能。</li>
            <li>模板内容会提供给{YOUTU_BRAND_NAME}读取，请勿上传不应被用户或 AI 使用的信息。</li>
          </ul>
        </aside>
      </div>
    </main>
  );
}
